import { FormEvent, useEffect, useState } from "react";
import {
  useAdminUsers,
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useResetUserPassword,
} from "@/hooks/useAdminUsers";
import { useRoles } from "@/hooks/useRoles";
import { usePermissions } from "@/hooks/usePermissions";
import { AdminUser } from "@/types/rbac";
import { PageHeader, Button } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

interface UserFormState {
  username: string;
  password: string;
  email: string;
  displayName: string;
  roleId: string;
  isActive: boolean;
}

const emptyForm: UserFormState = {
  username: "",
  password: "",
  email: "",
  displayName: "",
  roleId: "",
  isActive: true,
};

const UsersManagement = () => {
  const [showInactive, setShowInactive] = useState(false);
  const { data: users, isLoading } = useAdminUsers(showInactive);
  const { data: roles } = useRoles();
  const { canCreate, canEdit, canDelete, isSuperadmin } = usePermissions();

  const createMutation = useCreateAdminUser();
  const updateMutation = useUpdateAdminUser();
  const deleteMutation = useDeleteAdminUser();
  const resetPasswordMutation = useResetUserPassword();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");

  const isSaving = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (editingUser) {
      setForm({
        username: editingUser.username,
        password: "",
        email: editingUser.email || "",
        displayName: editingUser.displayName || "",
        roleId: editingUser.roleId || "",
        isActive: editingUser.isActive,
      });
    } else {
      setForm(emptyForm);
    }
  }, [editingUser]);

  const openCreateModal = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (user: AdminUser) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingUser(null);
      setForm(emptyForm);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.username.trim()) return;

    try {
      if (editingUser) {
        await updateMutation.mutateAsync({
          userId: editingUser.userId,
          data: {
            email: form.email.trim() || null,
            displayName: form.displayName.trim() || null,
            roleId: form.roleId || null,
            isActive: form.isActive,
          },
        });
      } else {
        if (!form.password) return;
        await createMutation.mutateAsync({
          username: form.username.trim(),
          password: form.password,
          email: form.email.trim() || null,
          displayName: form.displayName.trim() || null,
          roleId: form.roleId || null,
        });
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save user:", error);
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await deleteMutation.mutateAsync(userId);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Failed to delete user:", error);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUserId || !newPassword) return;
    try {
      await resetPasswordMutation.mutateAsync({
        userId: resetPasswordUserId,
        newPassword,
      });
      setResetPasswordUserId(null);
      setNewPassword("");
    } catch (error) {
      console.error("Failed to reset password:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="page wide">
        <PageHeader
          label="Administration"
          title="Users"
          description="Manage admin portal users and their roles."
        />
        <div className="card">
          <div className="empty">
            <div className="empty-i">
              <Icon name="users" />
            </div>
            <b>Loading...</b>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page wide">
      <PageHeader
        label="Administration"
        title="Users"
        description="Manage admin portal users and their roles."
        actions={
          canCreate("users_management") ? (
            <Button onClick={openCreateModal}>
              <Icon name="plus" />
              Add User
            </Button>
          ) : undefined
        }
      />

      {users && users.length > 0 ? (
        <div className="card">
          <div className="tbar">
            <label className="cb-row">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ display: "none" }}
              />
              <span className={`cb${showInactive ? " on" : ""}`}>
                <Icon name="check" />
              </span>
              Show inactive users
            </label>
          </div>

          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>User</th>
                  <th style={{ width: "16%" }}>Role</th>
                  <th style={{ width: "110px" }}>Status</th>
                  <th style={{ width: "130px" }}>Last Login</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.userId}
                    className={!user.isActive ? "dim" : ""}
                    style={
                      deleteConfirmId === user.userId
                        ? { background: "var(--err-bg)" }
                        : undefined
                    }
                  >
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.displayName || user.username}
                            className="av"
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <span className={`av${user.isSuperadmin ? " g" : ""}`}>
                            {(user.displayName || user.username).charAt(0).toUpperCase()}
                          </span>
                        )}
                        <div>
                          <div
                            className="pri-cell"
                            style={{ display: "flex", alignItems: "center", gap: 6 }}
                          >
                            {user.displayName || user.username}
                            {user.userId.startsWith("env:") ? (
                              <span className="b def" title="Defined in server configuration; manage via environment, not here.">
                                Environment account
                              </span>
                            ) : (
                              user.isSuperadmin && <span className="b pri">Superadmin</span>
                            )}
                          </div>
                          <div className="sub-cell">{user.email || user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>{user.roleName || "—"}</td>
                    <td>
                      {user.isActive ? (
                        <span className="sdot on">
                          <i></i>Active
                        </span>
                      ) : (
                        <span className="sdot off">
                          <i></i>Inactive
                        </span>
                      )}
                    </td>
                    <td className="num">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td>
                      <div
                        className="acts"
                        style={
                          deleteConfirmId === user.userId ? { opacity: 1 } : undefined
                        }
                      >
                        {canEdit("users_management") && !user.userId.startsWith("env:") && !user.isSuperadmin && (
                          <>
                            <button
                              className="act"
                              title="Edit"
                              onClick={() => openEditModal(user)}
                            >
                              <Icon name="edit" />
                            </button>
                            <button
                              className="act"
                              title="Reset password"
                              onClick={() => setResetPasswordUserId(user.userId)}
                            >
                              <Icon name="key" />
                            </button>
                          </>
                        )}
                        {canEdit("users_management") && !user.userId.startsWith("env:") && user.isSuperadmin && isSuperadmin && (
                          <>
                            <button
                              className="act"
                              title="Edit"
                              onClick={() => openEditModal(user)}
                            >
                              <Icon name="edit" />
                            </button>
                            <button
                              className="act"
                              title="Reset password"
                              onClick={() => setResetPasswordUserId(user.userId)}
                            >
                              <Icon name="key" />
                            </button>
                          </>
                        )}
                        {canDelete("users_management") &&
                          !user.userId.startsWith("env:") &&
                          !user.isSuperadmin &&
                          (deleteConfirmId === user.userId ? (
                            <div
                              style={{
                                display: "flex",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "11.5px",
                                  color: "var(--err)",
                                  fontWeight: 640,
                                }}
                              >
                                Delete this user?
                              </span>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(user.userId)}
                                loading={deleteMutation.isPending}
                              >
                                Confirm
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirmId(null)}
                                disabled={deleteMutation.isPending}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <button
                              className="act d"
                              title="Delete"
                              onClick={() => setDeleteConfirmId(user.userId)}
                            >
                              <Icon name="trash" />
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="empty">
            <div className="empty-i">
              <Icon name="users" />
            </div>
            <b>No users found</b>
            {canCreate("users_management") && (
              <Button onClick={openCreateModal}>Create your first user</Button>
            )}
          </div>
        </div>
      )}

      {/* User Modal */}
      {isModalOpen && (
        <div
          className="ov open"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="mod">
            <div className="mod-h">
              <div className="tx">
                <h3>{editingUser ? "Edit User" : "Create User"}</h3>
                <p>{editingUser ? "Update user details." : "Add a new admin user."}</p>
              </div>
              <button className="icon-btn" onClick={closeModal} disabled={isSaving}>
                <Icon name="x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mod-b">
                <div className="fg">
                  <label className="fl">
                    Username <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, username: e.target.value }))
                    }
                    className="inp mono"
                    placeholder="johndoe"
                    required
                    disabled={isSaving || !!editingUser}
                  />
                  {editingUser && <p className="hint">Locked on edit.</p>}
                </div>

                {!editingUser && (
                  <div className="fg">
                    <label className="fl">
                      Password <span className="req">*</span>
                    </label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, password: e.target.value }))
                      }
                      className="inp"
                      placeholder="••••••••"
                      required
                      minLength={8}
                      disabled={isSaving}
                    />
                    <p className="hint">Minimum 8 characters</p>
                  </div>
                )}

                <div className="row c2">
                  <div className="fg">
                    <label className="fl">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className="inp"
                      placeholder="john@example.com"
                      disabled={isSaving}
                    />
                  </div>

                  <div className="fg">
                    <label className="fl">Display Name</label>
                    <input
                      type="text"
                      value={form.displayName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, displayName: e.target.value }))
                      }
                      className="inp"
                      placeholder="John Doe"
                      disabled={isSaving}
                    />
                  </div>
                </div>

                <div className="fg">
                  <label className="fl">Role</label>
                  <select
                    value={form.roleId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, roleId: e.target.value }))
                    }
                    className="sel"
                    disabled={isSaving}
                  >
                    <option value="">No role assigned</option>
                    {roles?.map((role) => (
                      <option key={role.roleId} value={role.roleId}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                {editingUser && (
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label className="cb-row">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, isActive: e.target.checked }))
                        }
                        style={{ display: "none" }}
                        disabled={isSaving}
                      />
                      <span className={`cb${form.isActive ? " on" : ""}`}>
                        <Icon name="check" />
                      </span>
                      Active
                    </label>
                  </div>
                )}
              </div>

              <div className="mod-f">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeModal}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isSaving}>
                  {editingUser ? "Save Changes" : "Create User"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUserId && (
        <div
          className="ov open"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setResetPasswordUserId(null);
              setNewPassword("");
            }
          }}
        >
          <div className="mod" style={{ maxWidth: 400 }}>
            <div className="mod-h">
              <div className="tx">
                <h3>Reset Password</h3>
                <p>Enter a new password for this user.</p>
              </div>
              <button
                className="icon-btn"
                onClick={() => {
                  setResetPasswordUserId(null);
                  setNewPassword("");
                }}
              >
                <Icon name="x" />
              </button>
            </div>

            <div className="mod-b">
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">
                  New Password <span className="req">*</span>
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="inp"
                  placeholder="••••••••"
                  minLength={8}
                />
                <p className="hint">Minimum 8 characters</p>
              </div>
            </div>

            <div className="mod-f">
              <Button
                variant="ghost"
                onClick={() => {
                  setResetPasswordUserId(null);
                  setNewPassword("");
                }}
                disabled={resetPasswordMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                loading={resetPasswordMutation.isPending}
                disabled={newPassword.length < 8}
              >
                Reset Password
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
